import React, { useState, useMemo } from 'react';
import { FiSearch, FiCheck } from 'react-icons/fi';

const FREQUENT_NAMES = [
  'Colombia', 'Argentina', 'Costa Rica', 'Venezuela', 'Chile',
  'Paraguay', 'Uruguay', 'España', 'Estados Unidos',
];

const ISO_TO_NAME = {
  CO: 'Colombia', AR: 'Argentina', CR: 'Costa Rica', VE: 'Venezuela',
  CL: 'Chile', PY: 'Paraguay', UY: 'Uruguay', ES: 'España', US: 'Estados Unidos',
  MX: 'México', BR: 'Brasil', PE: 'Perú', BO: 'Bolivia', EC: 'Ecuador',
  GT: 'Guatemala', HN: 'Honduras', SV: 'El Salvador', PA: 'Panamá',
  DO: 'República Dominicana', CA: 'Canadá',
};

// Find option by name, case-insensitive, partial match fallback
// o.label is a JSX element, so we use o.data.name (string) for comparison
const findOption = (options, name) => {
  if (!options?.length) return null;
  const lower = name.toLowerCase();
  const getName = (o) => (o.data?.name || '').toLowerCase();
  return (
    options.find(o => getName(o) === lower) ||
    options.find(o => getName(o).includes(lower)) ||
    options.find(o => lower.includes(getName(o)))
  );
};

const MobileOriginSelector = ({ options, value, onChange, onSelect, detectedCountryCode }) => {
  const [search, setSearch] = useState('');

  const detectedName = ISO_TO_NAME[detectedCountryCode] || null;

  // Detected country first, then the rest of the frequent list
  const frecuentes = useMemo(() => {
    if (!detectedName) return FREQUENT_NAMES;
    return [detectedName, ...FREQUENT_NAMES.filter(n => n !== detectedName)];
  }, [detectedName]);

  const searchResults = useMemo(() => {
    if (!search.trim()) return [];
    const s = search.toLowerCase();
    return (options || []).filter(o => (o.data?.name || '').toLowerCase().includes(s)).slice(0, 10);
  }, [options, search]);

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    if (onSelect) onSelect();
    setSearch('');
  };

  // País ya elegido (por ubicación o a mano): se muestra arriba con «Continuar»,
  // igual que el destino. Antes el detectado salía con ✓ pero NO contaba como
  // confirmado y «Siguiente» no avanzaba (Wilker, 17/10/2026, iPhone 16e).
  const elegido = (options || []).find(o => o.value === value) || null;

  // Misma fila que el destino (círculo a la derecha, fila marcada con barra),
  // pero de selección única.
  const renderItem = (key, opt, name) => {
    const isSelected = !!opt && value === opt.value;
    return (
      <button
        key={key}
        type="button"
        className={`sf-dest-list__item ${isSelected ? 'sf-dest-list__item--selected' : ''} ${!opt ? 'sf-origin-selector__list-item--disabled' : ''}`}
        onClick={() => opt && handleSelect(opt.value)}
        disabled={!opt}
      >
        <span className="sf-dest-list__item-name">{name}</span>
        <span className={`sf-dest-list__item-check ${isSelected ? 'sf-dest-list__item-check--visible' : ''}`}>
          <FiCheck />
        </span>
      </button>
    );
  };

  return (
    <div className="sf-origin-selector sf-dest-list">
      {elegido && (
        <div className="sf-dest-list__counter-row">
          <span className="sf-dest-list__counter">
            <FiCheck style={{ fontSize: '12px' }} />
            {elegido.data?.name || 'Seleccionado'}
          </span>
          {onSelect && (
            <button type="button" className="sf-dest-list__confirm-btn" onClick={() => onSelect()}>
              Continuar →
            </button>
          )}
        </div>
      )}

      {/* Search field */}
      <div className="sf-origin-selector__search-wrap">
        <FiSearch className="sf-origin-selector__search-icon" />
        <input
          type="text"
          className="sf-origin-selector__search-input"
          placeholder="Buscar país de residencia..."
          value={search}
          onChange={e => setSearch(e.target.value)}
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
        />
        {search && (
          <button type="button" className="sf-origin-selector__search-clear" onClick={() => setSearch('')}>
            ×
          </button>
        )}
      </div>

      <p className="sf-dest-list__hint">Toca tu país de residencia</p>

      {search ? (
        <>
          <span className="sf-origin-selector__frecuentes-label">RESULTADOS</span>
          <div className="sf-dest-list__items">
            {searchResults.length > 0
              ? searchResults.map(opt => renderItem(opt.value, opt, opt.data?.name || ''))
              : <p className="sf-origin-selector__no-results">No encontramos ese país</p>}
          </div>
        </>
      ) : (
        <>
          <span className="sf-origin-selector__frecuentes-label">FRECUENTES</span>
          <div className="sf-dest-list__items">
            {frecuentes.map((name) => renderItem(name, findOption(options, name), name))}
          </div>
        </>
      )}
    </div>
  );
};

export default MobileOriginSelector;
